'use client';
import React from 'react';

// Backend integration: GET /api/reports/driver-activity?range=7d
const driverData = [
  { id: 'drv-001', name: 'Travis Reyes', techId: 'TECH-01', unit: 'Unit-14', jobsCompleted: 9, totalMiles: 847, drivingHours: 24.3, idleHours: 3.1, speedEvents: 2, hardBrakeEvents: 0, avgSpeed: 58 },
  { id: 'drv-002', name: 'Maria Gutierrez', techId: 'TECH-02', unit: 'Unit-08', jobsCompleted: 8, totalMiles: 612, drivingHours: 19.8, idleHours: 1.4, speedEvents: 0, hardBrakeEvents: 1, avgSpeed: 52 },
  { id: 'drv-003', name: 'Darnell Brooks', techId: 'TECH-05', unit: 'Unit-05', jobsCompleted: 11, totalMiles: 994, drivingHours: 28.1, idleHours: 2.8, speedEvents: 1, hardBrakeEvents: 0, avgSpeed: 55 },
  { id: 'drv-004', name: 'Sofia Castillo', techId: 'TECH-06', unit: 'Unit-19', jobsCompleted: 7, totalMiles: 541, drivingHours: 17.4, idleHours: 0.9, speedEvents: 3, hardBrakeEvents: 2, avgSpeed: 61 },
  { id: 'drv-005', name: 'Kyle Odom', techId: 'TECH-03', unit: 'Unit-22', jobsCompleted: 5, totalMiles: 388, drivingHours: 14.2, idleHours: 11.4, speedEvents: 0, hardBrakeEvents: 0, avgSpeed: 48 },
  { id: 'drv-006', name: 'James Whitfield', techId: 'TECH-07', unit: 'Unit-03', jobsCompleted: 6, totalMiles: 421, drivingHours: 16.1, idleHours: 1.2, speedEvents: 0, hardBrakeEvents: 1, avgSpeed: 50 },
];

const safetyScore = (d: typeof driverData[0]) => {
  let score = 100;
  score -= d.speedEvents * 8;
  score -= d.hardBrakeEvents * 5;
  if (d.idleHours > 5) score -= 10;
  return Math.max(score, 0);
};

export default function DriverActivityReport({ dateRange }: { dateRange: string }) {
  return (
    <div className="space-y-5">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-sm font-semibold text-zinc-200">Driver Activity Summary — {dateRange}</h3>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-zinc-800">
                {['Driver', 'Unit', 'Jobs Done', 'Miles', 'Drive Hrs', 'Idle Hrs', 'Speed Events', 'Hard Brakes', 'Avg Speed', 'Safety Score'].map((h) => (
                  <th key={`drv-h-${h}`} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {driverData.map((d) => {
                let score = safetyScore(d);
                return (
                  <tr key={d.id} className="hover:bg-zinc-800/40 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-zinc-700 border border-zinc-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-zinc-300">{d.name.split(' ').map((n) => n[0]).join('')}</span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-zinc-200">{d.name}</p>
                          <p className="text-[10px] text-zinc-600 font-mono">{d.techId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-amber-400">{d.unit}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300 tabular-nums">{d.jobsCompleted}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300 tabular-nums">{d.totalMiles.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-300 tabular-nums">{d.drivingHours}h</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono tabular-nums ${d.idleHours > 5 ? 'text-red-400' : d.idleHours > 2 ? 'text-amber-400' : 'text-zinc-400'}`}>
                        {d.idleHours}h
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono tabular-nums ${d.speedEvents > 1 ? 'text-red-400' : d.speedEvents === 1 ? 'text-amber-400' : 'text-zinc-400'}`}>
                        {d.speedEvents}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono tabular-nums ${d.hardBrakeEvents > 1 ? 'text-red-400' : d.hardBrakeEvents === 1 ? 'text-amber-400' : 'text-zinc-400'}`}>
                        {d.hardBrakeEvents}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-400 tabular-nums">{d.avgSpeed} mph</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${score}%`,
                              backgroundColor: score >= 90 ? '#34D399' : score >= 75 ? '#F59E0B' : '#F87171',
                            }}
                          />
                        </div>
                        <span className={`text-xs font-mono font-bold tabular-nums ${score >= 90 ? 'text-emerald-400' : score >= 75 ? 'text-amber-400' : 'text-red-400'}`}>
                          {score}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}